import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Lock, RefreshCw, Trash2, Download, LogOut, Users, Loader2, Eye, EyeOff,
  Mail, Send, AlertTriangle, Sparkles, Check
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'bb_admin_token';

export default function Admin() {
  const { toast } = useToast();
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [authed, setAuthed] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const fetchSubscribers = useCallback(async (t) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/subscribers`, {
        headers: { 'X-Admin-Token': t },
      });
      setSubscribers(data.subscribers || []);
      setTotal(data.total || 0);
      setAuthed(true);
      sessionStorage.setItem(TOKEN_KEY, t);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast({ title: 'Token invalide', description: 'Vérifiez votre token administrateur.' });
        sessionStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      } else {
        toast({ title: 'Erreur', description: 'Impossible de récupérer la liste.' });
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (token) fetchSubscribers(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allSelected = useMemo(
    () => subscribers.length > 0 && selected.size === subscribers.length,
    [subscribers, selected]
  );

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(subscribers.map((s) => s.id)));
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    fetchSubscribers(token.trim());
  };

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken('');
    setAuthed(false);
    setSubscribers([]);
    setTotal(0);
    setSelected(new Set());
  };

  const handleDelete = async (id, email) => {
    if (!window.confirm(`Supprimer l'inscription de ${email} ?`)) return;
    try {
      await axios.delete(`${API}/subscribers/${id}`, { headers: { 'X-Admin-Token': token } });
      setSubscribers((prev) => prev.filter((s) => s.id !== id));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setTotal((t) => t - 1);
      toast({ title: 'Supprimé', description: `${email} retiré de la liste.` });
    } catch {
      toast({ title: 'Erreur', description: 'Suppression échouée.' });
    }
  };

  const exportCSV = () => {
    const header = ['Email', 'Prénom', 'Vérifié', 'Sortie', 'Updates', 'Inscrit le'];
    const rows = subscribers.map((s) => [
      s.email,
      s.name || '',
      s.verified ? 'Oui' : 'Non',
      s.notify_launch ? 'Oui' : 'Non',
      s.notify_updates ? 'Oui' : 'Non',
      new Date(s.subscribed_at).toLocaleString('fr-FR'),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `buzzbump-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSend = async () => {
    const subj = subject.trim();
    const body = message.trim();
    if (!subj || !body) {
      toast({ title: 'Champs manquants', description: 'Sujet et message obligatoires.' });
      return;
    }
    if (selected.size === 0) {
      toast({ title: 'Aucun destinataire', description: 'Sélectionnez au moins un inscrit.' });
      return;
    }
    const confirmText = `Envoyer cet email à ${selected.size} destinataire(s) ?\n\nObjet : ${subj}\n\nIls le recevront depuis contact.buzzbump@gmail.com.`;
    if (!window.confirm(confirmText)) return;

    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/broadcast`,
        { subject: subj, message: body, recipient_ids: Array.from(selected) },
        { headers: { 'X-Admin-Token': token } }
      );
      toast({
        title: 'Email envoyé !',
        description: `${data.sent} email(s) envoyé(s)${data.failed?.length ? `, ${data.failed.length} échec(s)` : ''}.`,
      });
      setSubject('');
      setMessage('');
      setSelected(new Set());
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Erreur lors de l\'envoi.';
      toast({ title: 'Échec de l\'envoi', description: detail });
    } finally {
      setSending(false);
    }
  };

  if (!authed) {
    return (
      <section className="min-h-[70vh] flex items-center justify-center px-6 py-20">
        <form onSubmit={handleLogin} className="w-full max-w-md p-8 rounded-2xl border border-white/10 bg-neutral-900/60">
          <div className="mx-auto h-14 w-14 grid place-items-center rounded-md bg-yellow-400/15 text-yellow-400 mb-5">
            <Lock size={22} />
          </div>
          <h1 className="text-2xl font-bold text-white text-center mb-2">Espace administrateur</h1>
          <p className="text-neutral-400 text-sm text-center mb-7">Saisissez votre token pour accéder à la liste des inscrits.</p>
          <label className="block text-sm text-neutral-300 mb-2">Token administrateur</label>
          <div className="relative">
            <input
              type={showToken ? 'text' : 'password'}
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="w-full pl-4 pr-12 py-3 rounded-md bg-neutral-950 border border-white/10 text-white placeholder-neutral-500 focus:outline-none focus:border-yellow-400/60 focus:ring-2 focus:ring-yellow-400/20"
              placeholder="Votre token secret"
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              onClick={() => setShowToken((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-yellow-400"
              aria-label="Afficher / masquer le token"
            >
              {showToken ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-yellow-400 text-neutral-900 font-semibold hover:bg-yellow-300 transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />}
            Se connecter
          </button>
        </form>
      </section>
    );
  }

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Users className="text-yellow-400" /> Inscrits
          </h1>
          <p className="text-neutral-400 mt-1">
            {total} inscription{total > 1 ? 's' : ''} · <span className="text-yellow-400">{selected.size}</span> sélectionné{selected.size > 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => fetchSubscribers(token)} disabled={loading} className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/15 text-neutral-200 hover:border-yellow-400 hover:text-yellow-400 transition-colors disabled:opacity-50">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Actualiser
          </button>
          <button onClick={exportCSV} disabled={!subscribers.length} className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/15 text-neutral-200 hover:border-yellow-400 hover:text-yellow-400 transition-colors disabled:opacity-50">
            <Download size={16} /> Export CSV
          </button>
          <button onClick={handleLogout} className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/15 text-neutral-300 hover:border-red-400 hover:text-red-400 transition-colors">
            <LogOut size={16} /> Déconnexion
          </button>
        </div>
      </div>

      {/* Composer panel (always visible) */}
      <div className="mb-8 rounded-2xl border border-yellow-400/20 bg-neutral-900/40 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-gradient-to-r from-neutral-900 to-neutral-950">
          <div className="h-10 w-10 grid place-items-center rounded-md bg-yellow-400/15 text-yellow-400">
            <Mail size={18} />
          </div>
          <div className="flex-1">
            <h2 className="text-white font-semibold flex items-center gap-1.5">
              Composer un email <Sparkles size={14} className="text-yellow-400" />
            </h2>
            <p className="text-xs text-neutral-400">
              Depuis <span className="text-yellow-400">contact.buzzbump@gmail.com</span> · {selected.size} destinataire{selected.size > 1 ? 's' : ''} sélectionné{selected.size > 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm text-neutral-300 mb-2">
              Objet <span className="text-yellow-400">*</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-4 py-2.5 rounded-md bg-neutral-950 border border-white/10 text-white placeholder-neutral-500 focus:outline-none focus:border-yellow-400/60 focus:ring-2 focus:ring-yellow-400/20"
              placeholder="Sujet de l'email"
              maxLength={200}
              disabled={sending}
            />
          </div>
          <div>
            <label className="block text-sm text-neutral-300 mb-2">
              Message <span className="text-yellow-400">*</span>
            </label>
            <textarea
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full px-4 py-2.5 rounded-md bg-neutral-950 border border-white/10 text-white placeholder-neutral-500 focus:outline-none focus:border-yellow-400/60 focus:ring-2 focus:ring-yellow-400/20 leading-relaxed resize-y"
              placeholder="Écris ton message ici... (sauts de ligne préservés)"
              maxLength={20000}
              disabled={sending}
            />
            <div className="mt-1 text-xs text-neutral-500 text-right">{message.length}/20000</div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              onClick={handleSend}
              disabled={sending || !subject.trim() || !message.trim() || selected.size === 0}
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-yellow-400 text-neutral-900 font-bold hover:bg-yellow-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-yellow-400/20"
            >
              {sending ? (
                <><Loader2 size={18} className="animate-spin" /> Envoi en cours...</>
              ) : (
                <><Send size={16} /> Envoyer à {selected.size} inscrit{selected.size > 1 ? 's' : ''}</>
              )}
            </button>
            <div className="flex-1 flex items-start gap-2 px-3 py-2 rounded-md border border-yellow-400/20 bg-yellow-400/5 text-xs text-yellow-200/90">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <span>BCC : les destinataires ne voient pas l'adresse des autres. Footer de désinscription ajouté automatiquement.</span>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-neutral-900/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-950/60 text-yellow-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 accent-yellow-400 cursor-pointer"
                    aria-label="Tout sélectionner"
                  />
                </th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Prénom</th>
                <th className="text-center px-4 py-3">Vérifié</th>
                <th className="text-center px-4 py-3">Sortie</th>
                <th className="text-center px-4 py-3">Updates</th>
                <th className="text-left px-4 py-3">Inscrit le</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {subscribers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-neutral-500">
                    {loading ? 'Chargement...' : 'Aucun inscrit pour le moment.'}
                  </td>
                </tr>
              ) : (
                subscribers.map((s) => {
                  const isSel = selected.has(s.id);
                  return (
                    <tr key={s.id} className={`border-t border-white/5 transition-colors ${isSel ? 'bg-yellow-400/[0.04]' : 'hover:bg-white/[0.02]'}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggleOne(s.id)}
                          className="h-4 w-4 accent-yellow-400 cursor-pointer"
                          aria-label={`Sélectionner ${s.email}`}
                        />
                      </td>
                      <td className="px-4 py-3 text-white break-all">{s.email}</td>
                      <td className="px-4 py-3 text-neutral-300">{s.name || <span className="text-neutral-600">—</span>}</td>
                      <td className="px-4 py-3 text-center">
                        {s.verified ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-500/15 text-green-400 border border-green-500/30">
                            <Check size={12} /> Oui
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-orange-500/10 text-orange-400 border border-orange-500/30">
                            En attente
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${s.notify_launch ? 'bg-yellow-400/15 text-yellow-300' : 'bg-neutral-800 text-neutral-500'}`}>
                          {s.notify_launch ? 'Oui' : 'Non'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${s.notify_updates ? 'bg-yellow-400/15 text-yellow-300' : 'bg-neutral-800 text-neutral-500'}`}>
                          {s.notify_updates ? 'Oui' : 'Non'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-neutral-400 whitespace-nowrap">{new Date(s.subscribed_at).toLocaleString('fr-FR')}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleDelete(s.id, s.email)}
                          className="p-2 rounded-md text-neutral-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          aria-label="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
